import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import AppDropdown from "../../../components/AppDropdown/AppDropdown";
import * as hrmsService from "../../../services/hrmsService";
import { FormDrawer, FormFields, resetMasterCache, withNulls } from "../hrForm";
import EmployeeSelect from "../EmployeeSelect";
import { EmptyState, Field, LoadState, PrimaryButton, SecondaryButton, Tabs } from "../hrComponents";
import { cardStyle, iconBtnStyle, inputStyle, statusBadge, statusLabel, tdStyle, thStyle, todayYmd, useLoad } from "../hrUi";

// LEV-01/02: leave types, and the policies that say how much of each an employee earns.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
const bool = (k, label) => ({ key: k, label, type: "checkbox" });

const typeFields = (creating) => [
  { key: "code", label: "Code", required: true, max: 16, disabled: !creating, hint: creating ? "Letters, digits and underscores, e.g. ANNUAL" : "The code cannot change" },
  { key: "name", label: "Name", required: true, max: 100 },
  { key: "color", label: "Colour (#rrggbb)", max: 9 },
  bool("is_paid", "Paid leave"),
  bool("allow_half_day", "Half days allowed"),
  bool("probation_eligible", "Available during probation"),
  bool("count_weekly_offs", "Weekly offs inside the leave count as leave"),
  bool("count_holidays", "Holidays inside the leave count as leave"),
  bool("allow_negative", "Allow a negative balance"),
  { key: "negative_limit", label: "Negative balance limit (days)", type: "number", step: "0.5", min: 0 },
  { key: "attachment_after_days", label: "Supporting document needed after (days)", type: "number", step: "0.5", min: 0 },
  { key: "gender_eligibility", label: "Only for", type: "select", options: [{ label: "Male", value: "MALE" }, { label: "Female", value: "FEMALE" }] },
  bool("optional_holiday_only", "Only on optional holidays"),
  bool("is_comp_off", "Compensatory off"),
  { key: "comp_off_expiry_days", label: "Comp-off expires after (days)", type: "number", min: 1 },
];

function Types() {
  const [all, setAll] = useState(false);
  const { data, loading, error, reload } = useLoad(() => hrmsService.listLeaveTypes(all), [all]);
  const [edit, setEdit] = useState(null);
  const rows = data || [];
  const toggle = async (t) => {
    const res = await hrmsService.saveLeaveType(t.id, { status: t.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" });
    if (res?.success) reload();
    else toast.error(hrmsService.responseMessage(res));
  };
  const keys = typeFields(false).map((f) => f.key).filter((k) => k !== "code");
  return (
    <>
      <div className="d-flex align-items-center gap-3 mb-3">
        <PrimaryButton onClick={() => setEdit({ row: null })}>Add leave type</PrimaryButton>
        <div className="form-check mb-0">
          <input id="lt-all" type="checkbox" className="form-check-input" checked={all} onChange={(e) => setAll(e.target.checked)} />
          <label htmlFor="lt-all" className="form-check-label" style={{ fontSize: 13 }}>
            Show inactive
          </label>
        </div>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No leave types" text="Apply the standard template above to get started." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  {["Code", "Name", "Paid", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id}>
                    <td style={tdStyle}>
                      <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 5, background: t.color || "#999", marginRight: 8 }} />
                      {t.code}
                    </td>
                    <td style={tdStyle}>{t.name}</td>
                    <td style={tdStyle}>{t.is_paid ? "Paid" : "Unpaid"}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(t.status)}>{statusLabel(t.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" style={linkBtn} onClick={() => setEdit({ row: t })}>
                        Edit
                      </button>{" "}
                      <button type="button" style={linkBtn} onClick={() => toggle(t)}>
                        {t.status === "ACTIVE" ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={!!edit}
        title={edit?.row ? "Edit leave type" : "Add leave type"}
        fields={typeFields(!edit?.row)}
        initial={edit?.row || { is_paid: true, allow_half_day: true, probation_eligible: true, color: "#16a34a" }}
        onHide={() => setEdit(null)}
        onSave={(clean, raw) => (edit?.row ? hrmsService.saveLeaveType(edit.row.id, withNulls(raw, keys)) : hrmsService.saveLeaveType(null, clean))}
        onDone={() => {
          resetMasterCache();
          reload();
        }}
      />
    </>
  );
}

/* ------------------------------ policies ------------------------------ */

const ACCRUALS = [
  { label: "Whole year up front", value: "YEARLY_UPFRONT" },
  { label: "Monthly", value: "MONTHLY" },
  { label: "By days worked", value: "PER_DAYS_WORKED" },
];

// The rule fields this editor shows. Anything else a rule carries (service tiers, caps) is passed back untouched.
const ruleFields = (rule) => [
  { key: "accrual_type", label: "Accrual", type: "select", options: ACCRUALS },
  { key: "annual_quota", label: "Days per year", type: "number", step: "0.5", min: 0 },
  ...(rule.accrual_type === "PER_DAYS_WORKED" ? [{ key: "worked_days_per_credit", label: "Days worked for one day of leave", type: "number", min: 1 }] : []),
  { key: "accrue_from", label: "Accrue from", type: "select", options: [{ label: "Joining date", value: "JOINING" }, { label: "Confirmation", value: "CONFIRMATION" }] },
  { key: "carry_forward_cap", label: "Carry forward at most (days)", type: "number", step: "0.5", min: 0 },
  { key: "prorate_joiners", label: "Pro-rate for people who join mid-year", type: "checkbox" },
];

function PolicyDrawer({ policyId, show, onHide, types, onDone }) {
  const detail = useLoad(() => hrmsService.getLeavePolicy(policyId), [policyId], { enabled: !!policyId && show });
  const [form, setForm] = useState({ name: "", leave_year_type: "CALENDAR", year_start_month: 4 });
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (!policyId) {
      setForm({ name: "", leave_year_type: "CALENDAR", year_start_month: 4 });
      setRules([]);
    } else if (detail.data) {
      setForm({ name: detail.data.name, leave_year_type: detail.data.leave_year_type, year_start_month: detail.data.year_start_month || 4 });
      setRules(detail.data.rules || []);
    }
  }, [show, policyId, detail.data]);

  const setRule = (i, k, v) => setRules((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const used = new Set(rules.map((r) => r.leave_type_id));
  const free = types.filter((t) => !used.has(t.id));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, year_start_month: form.leave_year_type === "FINANCIAL" ? Number(form.year_start_month) : undefined, rules: rules.map(({ leave_type_code, leave_type_name, ...r }) => r) };
      const res = await hrmsService.saveLeavePolicy(policyId, body);
      if (res?.success) {
        toast.success(res.message || "Saved");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title={policyId ? "Edit leave policy" : "New leave policy"}
      width={620}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !form.name.trim()}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <LoadState loading={!!policyId && detail.loading} error={detail.error} onRetry={detail.reload} rows={3}>
        <FormFields
          values={form}
          onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
          fields={[
            { key: "name", label: "Policy name", required: true, max: 100 },
            { key: "leave_year_type", label: "Leave year", type: "select", options: [{ label: "Calendar year (Jan – Dec)", value: "CALENDAR" }, { label: "Financial year", value: "FINANCIAL" }] },
            ...(form.leave_year_type === "FINANCIAL" ? [{ key: "year_start_month", label: "Year starts in month (1–12)", type: "number", min: 1 }] : []),
          ]}
        />
        <h6 className="mt-3">Leave types in this policy</h6>
        {policyId ? <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 8 }}>Saving replaces the rules. Balances already earned are not changed; new rules apply from the next accrual.</div> : null}
        {rules.map((r, i) => (
          <div key={r.leave_type_id} style={{ ...cardStyle, padding: 14, marginBottom: 12, border: "1px solid #E5E7EB", boxShadow: "none" }}>
            <div className="d-flex justify-content-between mb-2">
              <strong>{r.leave_type_name || types.find((t) => t.id === r.leave_type_id)?.name}</strong>
              <button type="button" style={linkBtn} onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}>
                Remove
              </button>
            </div>
            {r.tiers && r.tiers.length ? <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 8 }}>Has service-based tiers (kept as they are).</div> : null}
            <FormFields columns={2} values={{ ...r, prorate_joiners: !!r.prorate_joiners }} onChange={(k, v) => setRule(i, k, v)} fields={ruleFields(r)} />
          </div>
        ))}
        {free.length ? (
          <Field label="Add a leave type">
            <AppDropdown
              value={null}
              options={free.map((t) => ({ label: t.name, value: t.id }))}
              onChange={(e) => e.value && setRules((rs) => [...rs, { leave_type_id: e.value, leave_type_name: types.find((t) => t.id === e.value)?.name, accrual_type: "YEARLY_UPFRONT", annual_quota: 0, accrue_from: "JOINING", prorate_joiners: true, carry_forward_cap: 0 }])}
              placeholder="Choose…"
            />
          </Field>
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}

function AssignDrawer({ policy, onHide, onDone }) {
  const [people, setPeople] = useState([]);
  const [pick, setPick] = useState(null);
  const [from, setFrom] = useState(todayYmd());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (policy) {
      setPeople([]);
      setPick(null);
      setFrom(todayYmd());
    }
  }, [policy]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.assignLeavePolicy(policy.id, { employee_ids: people.map((p) => p.id), effective_from: from });
      if (res?.success) {
        const failed = (res.data || []).filter((r) => !r.ok);
        failed.length ? toast.warn(failed[0].message) : toast.success(res.message || "Policy assigned");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={!!policy}
      onHide={() => !saving && onHide()}
      title={policy ? `Put people on “${policy.name}”` : ""}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !people.length}>
            Assign {people.length || ""}
          </PrimaryButton>
        </>
      }
    >
      <Field label="Add employees">
        <EmployeeSelect
          value={pick}
          exclude={people.map((p) => p.id)}
          onChange={(id, label) => {
            if (id) setPeople((ps) => [...ps, { id, label: label || "Employee" }]);
            setPick(null);
          }}
        />
      </Field>
      {people.map((p) => (
        <div key={p.id} className="d-flex justify-content-between py-1" style={{ fontSize: 14 }}>
          <span>{p.label}</span>
          <button type="button" style={linkBtn} onClick={() => setPeople((ps) => ps.filter((x) => x.id !== p.id))}>
            Remove
          </button>
        </div>
      ))}
      <Field label="From">
        <input type="date" className="form-control" style={inputStyle} value={from} onChange={(e) => setFrom(e.target.value)} />
      </Field>
      <div style={{ fontSize: 12, color: "#6B7280" }}>Recorded as a “leave policy change” in each person’s job history.</div>
    </SlidePanel>
  );
}

function Policies({ types }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listLeavePolicies(), []);
  const [edit, setEdit] = useState(null); // { id|null }
  const [assign, setAssign] = useState(null);
  const rows = data || [];
  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit({ id: null })}>New policy</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No leave policies" text="Apply the standard template above, or create one." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  {["Policy", "Leave year", "Types", "Employees", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td style={tdStyle}>{p.name}</td>
                    <td style={tdStyle}>{p.leave_year_type === "CALENDAR" ? "Calendar" : `Financial (from month ${p.year_start_month})`}</td>
                    <td style={tdStyle}>{p.rule_count}</td>
                    <td style={tdStyle}>{p.employee_count}</td>
                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" style={linkBtn} onClick={() => setEdit({ id: p.id })}>
                        Edit
                      </button>{" "}
                      <button type="button" style={linkBtn} onClick={() => setAssign(p)}>
                        Assign
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <PolicyDrawer show={!!edit} policyId={edit?.id} types={types} onHide={() => setEdit(null)} onDone={() => { resetMasterCache(); reload(); }} />
      <AssignDrawer policy={assign} onHide={() => setAssign(null)} onDone={reload} />
    </>
  );
}

export default function LeaveSetupTab() {
  const [tab, setTab] = useState("types");
  const [applying, setApplying] = useState(false);
  const types = useLoad(() => hrmsService.listLeaveTypes(false), []);
  const [key, setKey] = useState(0);

  const applyTemplate = async () => {
    setApplying(true);
    try {
      const res = await hrmsService.applyLeaveTemplate({});
      if (res?.success) {
        toast.success(res.message || "Standard leave set up");
        resetMasterCache();
        setKey((k) => k + 1);
        types.reload();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <div style={{ ...cardStyle, padding: 16, marginBottom: 20 }} className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <strong>Standard leave for your country</strong>
          <div style={{ fontSize: 13, color: "#6B7280" }}>Adds the usual leave types and a policy in one step. Safe to run again: nothing is duplicated.</div>
        </div>
        <SecondaryButton onClick={applyTemplate} disabled={applying}>
          Apply standard leave
        </SecondaryButton>
      </div>
      <Tabs tabs={[{ key: "types", label: "Leave types" }, { key: "policies", label: "Leave policies" }]} active={tab} onChange={setTab} />
      {tab === "types" ? <Types key={key} /> : <Policies key={key} types={types.data || []} />}
    </>
  );
}
